/**
 * Run Controller — Provider routing for agent execution
 *
 * Detects the configured provider type and API key validity,
 * then dispatches to the appropriate chat-completions provider.
 * Shows a clear error when API key is missing.
 */
import type { Workspace } from '../types'
import type { AgentConfig, AgentRunController, TranscriptEvent } from '../agentTypes'
import {
    createOpenAiChatCompletionsRunController,
    type OpenAiChatCompletionsRunControllerDependencies,
} from '../providers/openai-chat-completions/provider'
import { createMockRunController } from './mockProvider'

export type AgentRunControllerDependencies = OpenAiChatCompletionsRunControllerDependencies

/**
 * Optional runtime dependencies for the agent run controller.
 *
 * Allows the caller to supply an explicit `fetch` implementation so both
 * provider HTTP traffic and MCP-backed tool calls route through the same
 * runtime-owned transport — enabling guest-side component-job execution
 * (§227) and making the controller more testable.
 */
export interface AgentRunControllerRuntimeDeps {
    /**
     * Runtime `fetch` implementation. When supplied, this is used instead of
     * the browser global `fetch` for:
     * - The chat-completions / responses provider POST
     * - All MCP remote-tool calls via `createAgentToolRegistry`
     *
     * Obtain from a guest runtime as `runtime.fetch` (see §71 / §222).
     * Defaults to the browser global `fetch` when omitted.
     */
    fetchImpl?: typeof fetch
}

/**
 * Create an agent run controller based on the agent config.
 * Routes to the real chat-completions provider when a valid API key is present,
 * yields an error event when key is missing.
 *
 * @param config         - Agent configuration (model, provider, tools, MCP servers).
 * @param workspace      - Active workspace session.
 * @param dependencies   - Optional runtime transport dependencies.
 *                         Supply `{ fetchImpl }` when embedding the controller in a
 *                         guest runtime so all HTTP/MCP traffic uses the guest fetch.
 */
export function createAgentRunController(
    config: AgentConfig,
    workspace: Workspace,
    dependencies: AgentRunControllerDependencies = {},
): AgentRunController {
    const model = config.model?.trim() ?? ''
    if (!model) {
        return {
            async *run(): AsyncIterableIterator<TranscriptEvent> {
                yield {
                    type: 'error',
                    message: '⚠️ Missing model — choose a model in the agent settings before chatting.',
                    retryable: false,
                    timestamp: Date.now(),
                }
            },
            abort() { /* nothing to abort */ },
        }
    }

    // Route based on API key type
    const apiKey = config.provider?.apiKey?.trim() ?? ''
    const isMock = apiKey === 'sk-mock'
    const isReal = !isMock && apiKey.length > 0 && apiKey !== '***REDACTED***'

    if (isMock) {
        return createMockRunController(config, workspace)
    }

    if (isReal && config.provider?.type === 'openai-chat-completions') {
        return createOpenAiChatCompletionsRunController(config, workspace, dependencies)
    }

    // No valid API key — return a controller that yields a clear error
    console.warn('[AgentRuntime] No valid API key configured')
    return {
        async *run(): AsyncIterableIterator<TranscriptEvent> {
            yield {
                type: 'error',
                message: '⚠️ Missing API key — open the agent config (⚙️) and add your API key to start chatting.',
                retryable: false,
                timestamp: Date.now(),
            }
        },
        abort() { /* nothing to abort */ },
    }
}
